import { HTMLAttributes, MouseEvent, ReactElement, ReactNode, useCallback, useState } from 'react';

export type ActionButtonHandler<T> = (data: T) => Promise<boolean | void> | boolean | void;

type Props = Omit<HTMLAttributes<HTMLButtonElement>, 'onClick'> & {
  loader?: ReactNode;
  onClick: ActionButtonHandler<any>;
  className?: string;
};

export default function ActionButton({
  loader = (
    <svg
      version='1.1'
      xmlns='http://www.w3.org/2000/svg'
      x='0px'
      y='0px'
      viewBox='0 0 60 80'
      enableBackground='new 0 0 0 0'
      xmlSpace='preserve'
      className='max-h-full'
    >
      <circle stroke='none' cx='6' cy='40' r='6'>
        <animateTransform
          attributeName='transform'
          dur='1s'
          type='translate'
          values='0 15 ; 0 -15; 0 15'
          repeatCount='indefinite'
          begin='0.1'
        />
      </circle>
      <circle stroke='none' cx='30' cy='40' r='6'>
        <animateTransform
          attributeName='transform'
          dur='1s'
          type='translate'
          values='0 10 ; 0 -10; 0 10'
          repeatCount='indefinite'
          begin='0.2'
        />
      </circle>
      <circle stroke='none' cx='54' cy='40' r='6'>
        <animateTransform
          attributeName='transform'
          dur='1s'
          type='translate'
          values='0 5 ; 0 -5; 0 5'
          repeatCount='indefinite'
          begin='0.3'
        />
      </circle>
    </svg>
  ),
  className = '',
  children,
  onClick = async () => false,
  ...props
}: Props): ReactElement {
  const [loading, setLoading] = useState(false);

  const handleClick = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      setLoading(true);
      const data = e.currentTarget.dataset;
      await onClick(data);
      setLoading(false);
    },
    [onClick],
  );

  return (
    <button
      {...props}
      className={`group ${className} ${loading ? 'loading' : ''}`}
      onClick={handleClick}
    >
      <span className='action-button-loader'>{loader}</span>
      {children}
    </button>
  );
}
